import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History as HistoryIcon, MapPin, Clock, Hash } from "lucide-react";
import { priorityColor } from "@/lib/api";

const BRANCH_COLOR = { FIRE: "#EE352E", EMS: "#00B894", POLICE: "#0984E3", PD: "#0984E3", WEATHER: "#FDCB6E" };
function normBranch(b) { const x = (b || "POLICE").toUpperCase(); return x === "PD" ? "POLICE" : x; }

function fmtDate(d) {
  // d -> YYYY-MM-DD in local tz
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  } catch { return iso; }
}
function fmtNice(d) {
  return d.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

export default function HistoryPage() {
  const today = new Date();
  const [selected, setSelected] = useState(today);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState({}); // {YYYY-MM-DD: count}

  // Fetch heat-map counts (6 month rolling)
  useEffect(() => {
    const from = new Date(); from.setDate(from.getDate() - 183);
    api.get("/incidents/history/days", { params: { from: fmtDate(from), to: fmtDate(today) } })
      .then((r) => {
        const map = {};
        (r.data.days || []).forEach((d) => { map[d.date] = d.count; });
        setDays(map);
      })
      .catch(() => setDays({}));
  }, []);

  // Fetch detail for selected day
  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get("/incidents/history", { params: { date: fmtDate(selected) } })
      .then((r) => setPages(r.data || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, [selected]);

  // Calendar modifiers — mark days that have history
  const modifiers = useMemo(() => {
    const has = Object.keys(days).map((k) => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m - 1, d);
    });
    return { has };
  }, [days]);

  const sixMonthsAgo = new Date(); sixMonthsAgo.setDate(sixMonthsAgo.getDate() - 183);

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="history-page">
      <div className="h-12 px-4 flex items-center justify-between border-b border-border bg-card/80 backdrop-blur shrink-0">
        <div className="flex items-center gap-3">
          <HistoryIcon className="w-4 h-4 text-primary" />
          <div className="font-mono text-xs tracking-widest text-primary">// PAGE-OUT HISTORY · ROLLING 6 MONTHS</div>
        </div>
        <Badge variant="outline" className="font-mono text-[10px]">
          {Object.values(days).reduce((a, b) => a + b, 0)} TOTAL PAGES
        </Badge>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[360px_1fr] gap-4 p-4 overflow-hidden">
        {/* Calendar */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono tracking-widest">CALENDAR</CardTitle>
            <CardDescription className="text-[11px]">
              Yellow-marked dates have pages on record. Tap any day.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => d && setSelected(d)}
              fromDate={sixMonthsAgo}
              toDate={today}
              modifiers={modifiers}
              modifiersClassNames={{ has: "calendar-has-history" }}
              showOutsideDays
              data-testid="history-calendar"
              className="rounded-md"
            />
            <div className="mt-3 font-mono text-[10px] text-muted-foreground flex items-center gap-3">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-yellow-400/70" /> has pages
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-primary/70" /> selected
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="overflow-hidden flex flex-col">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm font-mono tracking-widest" data-testid="history-day-title">
              {fmtNice(selected)} · {pages.length} page{pages.length === 1 ? "" : "s"}
            </CardTitle>
            <CardDescription className="text-[11px]">
              Complete log of every page-out generated on this date.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full" data-testid="history-list">
              {loading && (
                <div className="p-8 text-center text-muted-foreground font-mono text-xs">LOADING…</div>
              )}
              {!loading && pages.length === 0 && (
                <div className="p-8 text-center text-muted-foreground font-mono text-xs">No pages logged on this day.</div>
              )}
              {!loading && pages.map((p) => {
                const b = normBranch(p.branch);
                const pr = (p.priority === "critical" ? "high" : p.priority || "low").toLowerCase();
                const unitSet = new Set();
                for (const u of p.updates || []) { if (u.unit_id) unitSet.add(u.unit_id); }
                return (
                  <div key={p.id} className="border-b border-border/40 p-3 hover:bg-accent/30" data-testid={`history-row-${p.incident_number}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge style={{ background: BRANCH_COLOR[b], color: "#fff" }} className="font-bold tracking-widest">{b}</Badge>
                      <Badge style={{ background: priorityColor(pr), color: "#fff" }} className="font-bold tracking-widest">{pr.toUpperCase()}</Badge>
                      <span className="font-mono text-xs text-primary flex items-center gap-1"><Hash className="w-3 h-3" />{p.incident_number}</span>
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(p.created_at)}</span>
                    </div>
                    <div className="mt-1.5 font-semibold text-sm">{p.call_type}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3 h-3" /> {p.address}, {p.city}, {p.state}
                    </div>
                    {p.notes && (
                      <div className="mt-1 text-xs leading-snug">{p.notes}</div>
                    )}
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      {Array.from(unitSet).map((u) => (
                        <Badge key={u} variant="outline" className="font-mono text-[10px]">{u}</Badge>
                      ))}
                      <span className="font-mono text-[10px] text-muted-foreground ml-auto">Paged {p.page_count || 1}× · last {fmtTime(p.last_paged_at || p.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
